import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, fileUrl, fmtMoney, BACKEND_URL, openPdf } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Search, Plus, FileText, Trash2, Download, Plane, Hotel,
  TrendingUp, Calendar, Coins, Copy, Share2, CheckCircle2, X, ExternalLink, Bell,
  Clock, AlertTriangle, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { OptionBadge, StatusBadge } from "../components/quote/Badges";
import AcceptanceChart from "../components/quote/AcceptanceChart";

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-5 card-lift">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-sky-600">{label}</p>
        <p className="mt-2 text-2xl font-bold text-slate-900 tracking-tight">{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon size={18} className="text-white" />
      </div>
    </div>
  </div>
);

const QuoteCard = ({ q, onDelete, onDownload, onDuplicate, onShare }) => {
  const totalH = (q.hotels || []).reduce((s, h) => s + Number(h.price_total || 0), 0);
  const totalF = (q.flights || []).filter((f) => f.show_price).reduce((s, f) => s + Number(f.price_total || 0), 0);
  const total = totalH + totalF;
  const passengers = q.passengers || 1;
  const opts = (q.hotels || []).map((h) => h.option_label).filter(Boolean);
  return (
    <Card className="rounded-2xl overflow-hidden card-lift border-slate-200 bg-white p-0" data-testid={`quote-card-${q.id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <StatusBadge status={q.status || "draft"} />
              <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 font-mono-2 text-[10px] tracking-wider">{q.currency}</Badge>
              <span className="text-xs text-slate-500">{q.date}</span>
            </div>
            <h3 className="font-serif-display text-2xl font-bold text-slate-900 leading-tight truncate">{q.destination}</h3>
            <p className="text-sm text-slate-600 mt-1 truncate">{q.passenger_name}</p>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-4 text-xs text-slate-600">
          <span className="flex items-center gap-1.5"><Plane size={13} /> {q.flights?.length || 0} vuelo(s)</span>
          <span className="flex items-center gap-1.5"><Hotel size={13} /> {q.hotels?.length || 0} hotel(es)</span>
          <span className="flex items-center gap-1.5">👤 {passengers} pax</span>
        </div>

        {opts.length > 0 && (
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-slate-400 mr-1">Opciones</span>
            {opts.map((o, i) => <OptionBadge key={`${o}-${i}`} label={o} size="sm" />)}
          </div>
        )}

        {q.status === "accepted" && (
          <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs text-emerald-800 flex items-start gap-2">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Aceptada por {q.accepted_by_name || q.passenger_name}</p>
              {q.accepted_option && <p>Opción elegida: <b>{q.accepted_option}</b></p>}
              {q.accepted_message && <p className="italic mt-0.5">"{q.accepted_message}"</p>}
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-slate-100 flex items-end justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-400">
              {q.show_total === false ? "Desde" : "Total"}
            </p>
            <p className="text-lg font-bold text-amber-600 font-mono-2">{fmtMoney(total, q.currency)}</p>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => onShare(q)} data-testid={`button-share-${q.id}`} title="Compartir">
              <Share2 size={16} />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDuplicate(q)} data-testid={`button-duplicate-${q.id}`} title="Duplicar">
              <Copy size={16} />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDownload(q)} data-testid={`button-download-pdf-${q.id}`} title="Descargar PDF">
              <Download size={16} />
            </Button>
            <Link to={`/cotizacion/${q.id}`}>
              <Button size="sm" variant="ghost" data-testid={`button-edit-${q.id}`} title="Editar">
                <FileText size={16} />
              </Button>
            </Link>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`button-delete-quote-${q.id}`}>
                  <Trash2 size={16} />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar cotización?</AlertDialogTitle>
                  <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(q.id)} className="bg-rose-600 hover:bg-rose-700" data-testid={`confirm-delete-${q.id}`}>
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </Card>
  );
};

const AcceptancesBanner = ({ items, onDismiss, onView }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="fade-up rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 p-4 mb-6" data-testid="acceptances-banner">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center">
            <Bell size={15} className="text-white" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Nuevas confirmaciones</p>
            <p className="text-sm text-slate-700">{items.length} pasajero{items.length > 1 ? "s han" : " ha"} aceptado su cotización.</p>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="rounded-xl bg-white border border-emerald-100 px-3 py-2 flex items-center justify-between gap-2" data-testid={`accepted-item-${it.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">
                <span className="text-emerald-700">✓</span> {it.accepted_by_name || it.passenger_name}
                {it.accepted_option && <> — eligió opción <b>{it.accepted_option}</b></>}
              </p>
              {it.accepted_message && <p className="text-xs text-slate-500 italic truncate">"{it.accepted_message}"</p>}
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" variant="ghost" onClick={() => onView(it.id)} className="text-xs h-7"><ExternalLink size={12} className="mr-1" />Ver</Button>
              <Button size="sm" variant="ghost" onClick={() => onDismiss(it.id)} className="text-xs h-7 text-slate-500"><X size={14} /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ShareDialog = ({ quote, onClose }) => {
  const [token, setToken] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!quote) return;
    setLoading(true);
    // First load without body to keep existing expiration
    http.post(`/quotes/${quote.id}/share`, {})
      .then((r) => {
        setToken(r.data.share_token);
        setExpiresAt(r.data.share_expires_at || "");
      })
      .finally(() => setLoading(false));
  }, [quote]);

  const url = token ? `${window.location.origin}/p/${token}` : "";

  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  };

  const saveExpiration = async (newDate) => {
    setSaving(true);
    try {
      await http.post(`/quotes/${quote.id}/share`, { expires_at: newDate || null });
      setExpiresAt(newDate || "");
      toast.success(newDate ? `Link vence el ${newDate}` : "Sin fecha de expiración");
    } catch { toast.error("No se pudo actualizar la expiración"); }
    finally { setSaving(false); }
  };

  const revoke = async () => {
    await http.post(`/quotes/${quote.id}/revoke-share`);
    toast.success("Link revocado. Generá uno nuevo cuando quieras.");
    onClose();
  };

  const daysLeft = expiresAt
    ? Math.ceil((new Date(expiresAt + "T23:59:59").getTime() - Date.now()) / 86400000)
    : null;

  return (
    <Dialog open={!!quote} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Compartir cotización</DialogTitle>
          <DialogDescription>
            El pasajero podrá ver esta cotización sin necesidad de crear cuenta y aceptarla con un click.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-slate-500">Generando link…</p>
        ) : (
          <div className="space-y-4">
            <div className="relative">
              <Input value={url} readOnly className="rounded-xl h-11 font-mono-2 text-xs pr-24" data-testid="share-url" />
              <Button size="sm" onClick={copy} className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 rounded-lg" data-testid="share-copy-btn">
                <Copy size={14} className="mr-1" /> Copiar
              </Button>
            </div>

            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Fecha de expiración</Label>
                {expiresAt && daysLeft !== null && (
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    daysLeft < 0 ? "bg-rose-100 text-rose-700" : daysLeft < 3 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                  }`}>
                    {daysLeft < 0 ? "Vencido" : daysLeft === 0 ? "Vence hoy" : `${daysLeft} día${daysLeft > 1 ? "s" : ""}`}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={expiresAt || ""}
                  onChange={(e) => saveExpiration(e.target.value)}
                  disabled={saving}
                  className="rounded-lg h-9 flex-1"
                  data-testid="share-expiration-input"
                />
                {expiresAt && (
                  <Button variant="ghost" size="sm" onClick={() => saveExpiration("")} disabled={saving} className="rounded-lg h-9 text-xs" data-testid="share-expiration-clear">
                    Quitar
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-500">
                Después de esta fecha, el link deja de funcionar automáticamente. Sin fecha = no expira.
              </p>
            </div>

            <div className="flex gap-2">
              <a href={url} target="_blank" rel="noreferrer" className="flex-1">
                <Button variant="outline" className="w-full rounded-xl" data-testid="share-open-btn">
                  <ExternalLink size={14} className="mr-2" /> Abrir vista pública
                </Button>
              </a>
              <Button variant="ghost" onClick={revoke} className="text-rose-600 rounded-xl" data-testid="share-revoke-btn">
                Revocar
              </Button>
            </div>
            <p className="text-[11px] text-slate-500">
              Cualquiera con el link puede ver la cotización. Cuando la acepten recibirás una notificación en el panel.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PendingRemindersCard = ({ items, onOpenShare }) => {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="pending-empty">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
            <Clock size={15} className="text-amber-600" />
          </div>
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600">Recordatorios</p>
        </div>
        <h3 className="font-serif-display text-xl font-bold text-slate-900">Todo al día</h3>
        <p className="text-xs text-slate-500 mt-1">Cuando algún link compartido lleve varios días sin respuesta te aparecerá acá.</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="pending-reminders">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
          <Clock size={15} className="text-amber-600" />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600">Pendientes de respuesta</p>
          <h3 className="font-serif-display text-lg font-bold text-slate-900">Reenviá el link a {items.length} pasajero{items.length > 1 ? "s" : ""}</h3>
        </div>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2" data-testid={`pending-item-${it.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900 truncate">{it.passenger_name}</p>
              <p className="text-xs text-slate-500 truncate">
                {it.destination} · Enviado hace {it.days_since_shared} día{it.days_since_shared !== 1 ? "s" : ""}
                {it.expired && <span className="ml-2 inline-flex items-center gap-1 text-rose-600 font-semibold"><AlertTriangle size={11} /> Link vencido</span>}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => onOpenShare(it)} className="rounded-lg h-8 text-xs" data-testid={`pending-resend-${it.id}`}>
              <RefreshCw size={12} className="mr-1" /> Reenviar
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const HomePage = () => {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, top_destination: "—", this_month: 0, currencies: 0, unread_acceptances: 0 });
  const [acceptances, setAcceptances] = useState([]);
  const [optionsStats, setOptionsStats] = useState({ total_accepted: 0, distribution: [] });
  const [pending, setPending] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [shareTarget, setShareTarget] = useState(null);
  const navigate = useNavigate();

  const load = async (query = "") => {
    setLoading(true);
    try {
      const [list, s, acc, opts, pend] = await Promise.all([
        http.get("/quotes", { params: { q: query } }),
        http.get("/quotes/stats"),
        http.get("/quotes/recent-acceptances"),
        http.get("/quotes/stats/acceptance-options"),
        http.get("/quotes/pending-reminders", { params: { days: 3 } }),
      ]);
      setQuotes(list.data);
      setStats(s.data);
      setAcceptances((acc.data || []).filter((x) => x.agent_notified === false));
      setOptionsStats(opts.data || { total_accepted: 0, distribution: [] });
      setPending(pend.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]);

  const deleteQuote = async (id) => {
    await http.delete(`/quotes/${id}`);
    toast.success("Cotización eliminada");
    load(q);
  };

  const downloadPdf = (quote) => {
    openPdf(`/quotes/${quote.id}/pdf`, `cotizacion_${quote.passenger_name || quote.id}.pdf`)
      .catch(() => toast.error("No se pudo descargar el PDF"));
  };

  const duplicate = async (quote) => {
    try {
      const r = await http.post(`/quotes/${quote.id}/duplicate`);
      toast.success("Cotización duplicada. Editá los nuevos datos.");
      navigate(`/cotizacion/${r.data.id}`);
    } catch { toast.error("No se pudo duplicar"); }
  };

  const dismissAcceptance = async (id) => {
    await http.post(`/quotes/${id}/mark-notified`);
    setAcceptances((p) => p.filter((x) => x.id !== id));
    setStats((p) => ({ ...p, unread_acceptances: Math.max(0, (p.unread_acceptances || 0) - 1) }));
  };

  return (
    <div className="max-w-7xl">
      <header className="fade-up flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-sky-600 mb-2">Panel de agencia</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
            Historial de cotizaciones
          </h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            Crea, comparte y hacé seguimiento de cotizaciones en PDF con el branding de tu agencia.
          </p>
        </div>
        <Button onClick={() => navigate("/cotizacion/nueva")} className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-11 px-5" data-testid="button-create-quote">
          <Plus size={18} className="mr-2" /> Nueva cotización
        </Button>
      </header>

      <AcceptancesBanner
        items={acceptances}
        onDismiss={dismissAcceptance}
        onView={(id) => navigate(`/cotizacion/${id}`)}
      />

      <div className="fade-up-2 grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard label="Total" value={stats.total} icon={FileText} accent="bg-slate-900" />
        <StatCard label="Este mes" value={stats.this_month} icon={Calendar} accent="bg-sky-600" />
        <StatCard label="Destino top" value={stats.top_destination} icon={TrendingUp} accent="bg-amber-600" />
        <StatCard label="Nuevas confirmaciones" value={stats.unread_acceptances || 0} icon={CheckCircle2} accent="bg-emerald-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <AcceptanceChart data={optionsStats.distribution} totalAccepted={optionsStats.total_accepted} />
        <PendingRemindersCard items={pending} onOpenShare={(it) => setShareTarget(it)} />
      </div>

      <div className="fade-up-3 mb-6 relative max-w-xl">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Buscar por pasajero o destino…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="input-search-quote"
          className="pl-11 h-12 rounded-xl bg-white border-slate-200"
        />
      </div>

      {loading ? (
        <p className="text-slate-500">Cargando…</p>
      ) : quotes.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-12 text-center" data-testid="empty-state">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-sky-50 flex items-center justify-center mb-4">
            <Plane className="text-sky-600" size={24} />
          </div>
          <h3 className="font-serif-display text-2xl font-bold text-slate-900">Aún no hay cotizaciones</h3>
          <p className="text-slate-600 mt-2 mb-6">Crea tu primera cotización y descárgala en PDF profesional.</p>
          <Button onClick={() => navigate("/cotizacion/nueva")} className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl">
            <Plus size={18} className="mr-2" /> Crear cotización
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quotes.map((qu) => (
            <QuoteCard key={qu.id} q={qu}
              onDelete={deleteQuote}
              onDownload={downloadPdf}
              onDuplicate={duplicate}
              onShare={setShareTarget}
            />
          ))}
        </div>
      )}

      <ShareDialog quote={shareTarget} onClose={() => { setShareTarget(null); load(q); }} />
    </div>
  );
};

export default HomePage;
